/* R47 MASI cell art: three decorative science loops (membrane uptake, double helix, living cell) on one light Canvas engine.
   Decorative artwork only. It does not show measured data or simulate a clinical process.
   Autoplays while visible. Stops offscreen and in a hidden tab. Reduced motion draws one complete still frame. No controls. */
(() => {
  if (customElements.get('masi-cell-art')) return;
  const TAU = Math.PI * 2;
  const LOOPS = { uptake: 24, helix: 20, cell: 30 };
  const FPS = 30;

  // Deterministic randomness, so every frame and the still images match.
  const rng = seed => () => { seed |= 0; seed = seed + 0x6d2b79f5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const smooth = (a, b, v) => { const x = clamp((v - a) / (b - a), 0, 1); return x * x * (3 - 2 * x); };
  const frac = v => v - Math.floor(v);
  const rgba = (hex, a) => { const n = parseInt(hex.slice(1), 16); return `rgba(${n >> 16 & 255},${n >> 8 & 255},${n & 255},${a})`; };

  // Pearl sprite: a glassy sphere lit from the upper left. Drawn once, then reused.
  function pearl(size, core, rim, { highlight = .9, rimLight = .55, alpha = 1 } = {}) {
    const c = document.createElement('canvas'); c.width = c.height = size;
    const g = c.getContext('2d'); const r = size / 2;
    const body = g.createRadialGradient(r * .72, r * .64, r * .05, r, r, r);
    body.addColorStop(0, rgba('#ffffff', highlight * alpha)); body.addColorStop(.28, rgba(core, .92 * alpha)); body.addColorStop(.82, rgba(rim, .9 * alpha)); body.addColorStop(1, rgba(rim, .35 * alpha));
    g.fillStyle = body; g.beginPath(); g.arc(r, r, r * .96, 0, TAU); g.fill();
    const edge = g.createRadialGradient(r, r, r * .7, r, r, r);
    edge.addColorStop(0, 'rgba(255,255,255,0)'); edge.addColorStop(.92, rgba('#ffffff', rimLight * alpha)); edge.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = edge; g.beginPath(); g.arc(r, r, r, 0, TAU); g.fill();
    return c;
  }

  /* ---------- 1. Membrane uptake (white dosage section) ---------- */
  const uptake = {
    init(env) {
      const r = rng(47);
      env.head = pearl(64, '#e4edf3', '#6f8aa0', { highlight: 1, rimLight: .5 });
      const tints = ['#71ebff', '#ff4d86', '#64ffc3', '#e6ef3a', '#a8d500'];
      env.vesicleSprites = tints.map(t => pearl(160, t, t, { highlight: 1, rimLight: .9, alpha: .7 }));
      env.tints = tints;
      env.lanes = [.08, .19, .31, .44, .56, .68, .79, .91].map((x, i) => ({ x, phase: frac(i * .381966 + .07), tint: i % 5, drift: r() * TAU }));
      env.specks = Array.from({ length: 70 }, () => ({ x: r(), y: r(), a: r() * TAU, s: .6 + r() * 1.4, k: 1 + Math.floor(r() * 2) }));
    },
    draw(ctx, w, h, t, env) {
      const P = LOOPS.uptake, ph = TAU * t / P;
      const mid = h * .58, amp = h * .045, gap = Math.max(9, h * .038);
      const vr = clamp(h * .066, 12, 30);
      // Vesicle positions first: the membrane dimples around the ones crossing it.
      const ves = env.lanes.map(l => {
        const p = frac(l.phase + t / P); const u = 2 * p - 1;
        const y = mid + h * .6 * Math.sign(u) * Math.pow(Math.abs(u), 1.7);
        const x = w * l.x + w * .015 * Math.sin(TAU * p + l.drift);
        const fade = Math.pow(Math.sin(Math.PI * p), .7);
        return { x, y, u, p, fade, tint: l.tint };
      });
      const membraneY = x => {
        let y = mid + amp * Math.sin(TAU * x / w * 1.15 + ph) + amp * .45 * Math.sin(TAU * x / w * 2.6 - 2 * ph + 1.1);
        for (const v of ves) { const near = 1 - Math.min(1, Math.abs(v.u) / .32); if (near > 0) y += vr * 1.1 * near * near * Math.exp(-Math.pow((x - v.x) / (vr * 2.4), 2)); }
        return y;
      };
      ctx.clearRect(0, 0, w, h);
      // Interior wash below the membrane and a soft light at its centre.
      // Soft elliptical light around the membrane. It fades out inside the frame, so no edge shows on the white page.
      ctx.save(); ctx.translate(w / 2, mid + h * .08); ctx.scale(1, Math.min(1, h / w * 1.25));
      const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, w * .46);
      glow.addColorStop(0, 'rgba(113,235,255,.16)'); glow.addColorStop(.55, 'rgba(113,235,255,.06)'); glow.addColorStop(1, 'rgba(113,235,255,0)');
      ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(0, 0, w * .46, 0, TAU); ctx.fill(); ctx.restore();
      // Interior specks drift slowly.
      for (const s of env.specks) {
        const x = s.x * w + 6 * Math.sin(ph * s.k + s.a), y = mid + gap * 2 + s.y * (h - mid - gap * 2) + 5 * Math.cos(ph * s.k + s.a);
        ctx.fillStyle = `rgba(36,115,139,${.10 + .12 * s.s / 2})`; ctx.beginPath(); ctx.arc(x, y, s.s, 0, TAU); ctx.fill();
      }
      // Vesicles behind the membrane (below it, dissolving inward).
      const drawVesicle = v => {
        if (v.fade <= .01) return;
        const scale = 1 - .35 * smooth(0, 1, v.u);
        const size = vr * 2 * scale;
        ctx.globalAlpha = v.fade * (1 - .45 * smooth(.2, 1, v.u));
        ctx.drawImage(env.vesicleSprites[v.tint], v.x - size / 2, v.y - size / 2, size, size);
        ctx.strokeStyle = rgba(v.tint === 3 ? '#9aa31a' : env.tints[v.tint], .55); ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(v.x, v.y, size * .48, 0, TAU); ctx.stroke();
        ctx.globalAlpha = 1;
      };
      ves.filter(v => v.u > 0).forEach(drawVesicle);
      // The membrane surface recedes in perspective behind the cut edge: rows of heads, smaller and fainter with distance.
      const step = Math.max(8, w / 132), headSize = step * 1.05;
      for (let row = 6; row >= 1; row--) {
        const k = row / 6, lift = gap * (1.05 + row * .95), sz = headSize * (1 - .09 * row), off = (row % 2) * step / 2;
        for (let x = -step + off; x <= w + step; x += step * (1 + .04 * row)) {
          const y = membraneY(x + row * 9) - lift;
          const edge = smooth(0, w * .14, x) * smooth(w, w * .86, x);
          ctx.globalAlpha = (.62 - .5 * k) * edge;
          ctx.drawImage(env.head, x - sz / 2, y - gap - sz / 2, sz, sz);
        }
      }
      ctx.globalAlpha = 1;
      // Channel proteins spanning the bilayer.
      for (const cxr of [.23, .63]) {
        const x = w * cxr, y = membraneY(x), hh = gap * 2.9, ww = step * 2.3;
        const g = ctx.createLinearGradient(x - ww, 0, x + ww, 0);
        g.addColorStop(0, 'rgba(36,115,139,.10)'); g.addColorStop(.5, 'rgba(113,235,255,.32)'); g.addColorStop(1, 'rgba(36,115,139,.12)');
        ctx.fillStyle = g; ctx.strokeStyle = 'rgba(36,115,139,.35)'; ctx.lineWidth = 1;
        for (const side of [-1, 1]) { ctx.beginPath(); ctx.ellipse(x + side * ww * .42, y, ww * .42, hh / 2, 0, 0, TAU); ctx.fill(); ctx.stroke(); }
      }
      // Lipid bilayer cut edge: tails, then heads in two leaflets.
      ctx.lineCap = 'round';
      for (let x = -step; x <= w + step; x += step) {
        const y = membraneY(x);
        const edge = smooth(0, w * .1, x) * smooth(w, w * .9, x);
        ctx.strokeStyle = `rgba(11,31,58,${.16 * edge})`; ctx.lineWidth = .9;
        for (const side of [-1, 1]) {
          const y0 = y + side * gap, y1 = y + side * gap * .18;
          ctx.beginPath(); ctx.moveTo(x - 1.4, y0); ctx.quadraticCurveTo(x - 2.8, (y0 + y1) / 2, x - 1.2, y1); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(x + 1.4, y0); ctx.quadraticCurveTo(x + 2.8, (y0 + y1) / 2, x + 1.2, y1); ctx.stroke();
        }
      }
      for (let x = -step; x <= w + step; x += step) {
        const y = membraneY(x);
        const edge = smooth(0, w * .1, x) * smooth(w, w * .9, x);
        ctx.globalAlpha = .25 + .75 * edge;
        for (const side of [-1, 1]) ctx.drawImage(env.head, x - headSize / 2, y + side * gap - headSize / 2, headSize, headSize);
      }
      ctx.globalAlpha = 1;
      // Vesicles in front (still arriving from above).
      ves.filter(v => v.u <= 0).forEach(drawVesicle);
    }
  };

  /* ---------- 2. Double helix (navy Why MASI panel) ---------- */
  const helix = {
    init(env) {
      const r = rng(11);
      env.bead = pearl(96, '#bff7ff', '#2c7fa3', { highlight: 1, rimLight: .7 });
      env.beadWarm = pearl(96, '#e7dcff', '#6f5fb0', { highlight: 1, rimLight: .7 });
      env.motes = Array.from({ length: 34 }, () => ({ s: r(), o: (r() - .5) * 2.4, z: r(), size: .8 + r() * 2.2 }));
      env.pairs = Array.from({ length: 64 }, () => r() < .5 ? 0 : 1);
    },
    draw(ctx, w, h, t, env) {
      const P = LOOPS.helix, spin = TAU * t / P;
      ctx.clearRect(0, 0, w, h);
      const ax0 = { x: w * .12, y: h * 1.04 }, ax1 = { x: w * .9, y: -h * .04 };
      const dx = ax1.x - ax0.x, dy = ax1.y - ax0.y, L = Math.hypot(dx, dy);
      const d = { x: dx / L, y: dy / L }, n = { x: -d.y, y: d.x };
      const R = Math.min(w, h) * .15, pitch = R * 3.6, turns = L / pitch, perTurn = 12;
      const count = Math.ceil(turns * perTurn), samples = 7;
      // Ambient light along the axis.
      ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i <= 4; i++) {
        const s = .15 + i * .18, cx = ax0.x + dx * s, cy = ax0.y + dy * s;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 2.6);
        g.addColorStop(0, 'rgba(113,235,255,.07)'); g.addColorStop(1, 'rgba(113,235,255,0)');
        ctx.fillStyle = g; ctx.fillRect(cx - R * 3, cy - R * 3, R * 6, R * 6);
      }
      ctx.globalCompositeOperation = 'source-over';
      const at = (s, strand) => {
        const theta = TAU * s * turns + spin + (strand ? 2.3 : 0);
        const z = Math.sin(theta), k = 1 + .2 * z;
        const cx = ax0.x + dx * s, cy = ax0.y + dy * s;
        return { x: cx + n.x * R * Math.cos(theta) * k, y: cy + n.y * R * Math.cos(theta) * k, z, k };
      };
      const items = [];
      for (let strand = 0; strand < 2; strand++) {
        let prev = at(0, strand);
        for (let i = 1; i <= count * samples; i++) {
          const s = i / (count * samples), p = at(s, strand);
          items.push({ z: (p.z + prev.z) / 2, type: 'seg', a: prev, b: p, s });
          prev = p;
        }
      }
      for (let i = 0; i <= count; i++) {
        const s = i / count, a = at(s, 0), b = at(s, 1);
        items.push({ z: (a.z + b.z) / 2 - .05, type: 'rod', a, b, s, kind: env.pairs[i % env.pairs.length] });
        items.push({ z: a.z + .01, type: 'bead', p: a, s, warm: false });
        items.push({ z: b.z + .01, type: 'bead', p: b, s, warm: true });
      }
      items.sort((p, q) => p.z - q.z);
      ctx.lineCap = 'round';
      for (const it of items) {
        const fade = smooth(0, .12, it.s) * smooth(1, .88, it.s);
        const depth = (it.z + 1) / 2;
        if (it.type === 'seg') {
          ctx.strokeStyle = `rgba(${Math.round(60 + 157 * depth)},${Math.round(130 + 118 * depth)},${Math.round(165 + 90 * depth)},${(.25 + .7 * depth) * fade})`;
          ctx.lineWidth = R * .085 * (it.a.k + it.b.k) / 2;
          ctx.beginPath(); ctx.moveTo(it.a.x, it.a.y); ctx.lineTo(it.b.x, it.b.y); ctx.stroke();
        } else if (it.type === 'rod') {
          const g = ctx.createLinearGradient(it.a.x, it.a.y, it.b.x, it.b.y);
          const c1 = it.kind ? '#71ebff' : '#64ffc3', c2 = it.kind ? '#b79cff' : '#71ebff';
          g.addColorStop(0, rgba(c1, (.12 + .4 * depth) * fade)); g.addColorStop(.5, rgba('#d9f8ff', (.05 + .2 * depth) * fade)); g.addColorStop(1, rgba(c2, (.12 + .4 * depth) * fade));
          ctx.strokeStyle = g; ctx.lineWidth = R * .055;
          ctx.beginPath(); ctx.moveTo(it.a.x, it.a.y); ctx.lineTo(it.b.x, it.b.y); ctx.stroke();
        } else {
          const size = R * .2 * it.p.k;
          ctx.globalAlpha = (.3 + .7 * depth) * fade;
          ctx.drawImage(it.warm ? env.beadWarm : env.bead, it.p.x - size / 2, it.p.y - size / 2, size, size);
          ctx.globalAlpha = 1;
        }
      }
      // Light motes drifting up the axis. They fade at both ends of their path, so the loop has no jump.
      for (const m of env.motes) {
        const s = frac(m.s + t / P), cx = ax0.x + dx * s + n.x * R * m.o * 1.4, cy = ax0.y + dy * s + n.y * R * m.o * 1.4;
        const a = Math.sin(Math.PI * s) * (.25 + .5 * m.z);
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, m.size * 3);
        g.addColorStop(0, `rgba(217,248,255,${a})`); g.addColorStop(1, 'rgba(217,248,255,0)');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, m.size * 3, 0, TAU); ctx.fill();
      }
    }
  };

  /* ---------- 3. Living cell (navy More life section) ---------- */
  const cell = {
    init(env) {
      const r = rng(2026);
      const tints = ['#71ebff', '#64ffc3', '#b79cff', '#ff4d86', '#f6ff48', '#a8d500'];
      env.mito = Array.from({ length: 7 }, (_, i) => ({ a: i / 7 * TAU + r() * .4, rad: .64 + r() * .14, len: .17 + r() * .06, dir: i % 2 ? 1 : -1, tint: tints[i % tints.length], wob: r() * TAU }));
      env.vesicles = Array.from({ length: 16 }, (_, i) => ({ a: r() * TAU, rad: .45 + r() * .42, size: .025 + r() * .03, k: (i % 3) + 1, dir: i % 2 ? 1 : -1, sprite: i % 3 }));
      env.vSprites = ['#71ebff', '#64ffc3', '#b79cff'].map(t => pearl(96, t, t, { highlight: .9, rimLight: .8, alpha: .6 }));
      env.specks = Array.from({ length: 150 }, () => ({ a: r() * TAU, rad: Math.sqrt(r()) * .9, k: 1 + Math.floor(r() * 3), w: r() * TAU, s: .4 + r() * 1.1 }));
      env.motes = Array.from({ length: 22 }, () => ({ a: r() * TAU, rad: 1.07 + r() * .12, s: .6 + r() * 1.6, k: r() < .5 ? 1 : -1 }));
    },
    draw(ctx, w, h, t, env) {
      const P = LOOPS.cell, ph = TAU * t / P;
      const cx = w / 2, cy = h / 2, R = Math.min(w, h) * .4;
      ctx.clearRect(0, 0, w, h);
      const radius = a => R * (1 + .022 * Math.sin(2 * a + ph) + .016 * Math.sin(3 * a - 2 * ph + 1.3) + .01 * Math.sin(5 * a + 3 * ph + .7));
      const outline = (scale = 1) => {
        ctx.beginPath();
        for (let i = 0; i <= 180; i++) { const a = i / 180 * TAU, rr = radius(a) * scale; i ? ctx.lineTo(cx + rr * Math.cos(a), cy + rr * Math.sin(a)) : ctx.moveTo(cx + rr * Math.cos(a), cy + rr * Math.sin(a)); }
        ctx.closePath();
      };
      // Outer halo.
      const halo = ctx.createRadialGradient(cx, cy, R * .85, cx, cy, Math.min(w, h) * .5);
      halo.addColorStop(0, 'rgba(113,235,255,.16)'); halo.addColorStop(.5, 'rgba(183,156,255,.06)'); halo.addColorStop(1, 'rgba(113,235,255,0)');
      ctx.fillStyle = halo; ctx.fillRect(0, 0, w, h);
      // Extracellular motes.
      for (const m of env.motes) {
        const a = m.a + m.k * ph, rr = R * m.rad;
        ctx.fillStyle = `rgba(217,248,255,${.18 + .12 * Math.sin(ph * 2 + m.a)})`;
        ctx.beginPath(); ctx.arc(cx + rr * Math.cos(a), cy + rr * Math.sin(a), m.s, 0, TAU); ctx.fill();
      }
      // Cytoplasm: translucent body, brighter towards the rim like light through a membrane.
      ctx.save(); outline(); ctx.clip();
      const body = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 1.05);
      body.addColorStop(0, 'rgba(8,24,44,.92)'); body.addColorStop(.55, 'rgba(12,44,68,.78)'); body.addColorStop(.86, 'rgba(22,86,112,.62)'); body.addColorStop(1, 'rgba(60,150,170,.55)');
      ctx.fillStyle = body; ctx.fillRect(cx - R * 1.2, cy - R * 1.2, R * 2.4, R * 2.4);
      const key = ctx.createRadialGradient(cx - R * .45, cy - R * .5, 0, cx - R * .45, cy - R * .5, R * .9);
      key.addColorStop(0, 'rgba(217,248,255,.12)'); key.addColorStop(1, 'rgba(217,248,255,0)');
      ctx.fillStyle = key; ctx.fillRect(cx - R * 1.2, cy - R * 1.2, R * 2.4, R * 2.4);
      for (const s of env.specks) {
        const a = s.a + .06 * Math.sin(s.k * ph + s.w), rr = R * (s.rad + .02 * Math.cos(s.k * ph + s.w));
        ctx.fillStyle = `rgba(190,240,255,${.10 + .14 * s.s})`; ctx.beginPath(); ctx.arc(cx + rr * Math.cos(a), cy + rr * Math.sin(a), s.s, 0, TAU); ctx.fill();
      }
      // Endoplasmic folds around the nucleus: fine wavy arcs.
      ctx.lineWidth = 1;
      for (let j = 0; j < 3; j++) {
        ctx.strokeStyle = `rgba(113,235,255,${.13 - j * .03})`;
        ctx.beginPath();
        for (let i = 0; i <= 120; i++) {
          const a = -2.2 + j * 1.9 + i / 120 * 2.1, rr = R * (.46 + j * .05 + .012 * Math.sin(a * 9 + ph * (j + 1)));
          i ? ctx.lineTo(cx + rr * Math.cos(a), cy + rr * Math.sin(a)) : ctx.moveTo(cx + rr * Math.cos(a), cy + rr * Math.sin(a));
        }
        ctx.stroke();
      }
      // Mitochondria drift on slow orbits, each with its inner folds.
      for (const m of env.mito) {
        const a = m.a + m.dir * ph, rr = R * (m.rad + .025 * Math.sin(2 * ph + m.wob));
        const x = cx + rr * Math.cos(a), y = cy + rr * Math.sin(a), rot = a + Math.PI / 2 + .35 * Math.sin(ph + m.wob);
        const len = R * m.len, wid = R * .062;
        ctx.save(); ctx.translate(x, y); ctx.rotate(rot);
        const g = ctx.createLinearGradient(0, -wid, 0, wid);
        g.addColorStop(0, rgba(m.tint, .42)); g.addColorStop(.5, rgba(m.tint, .16)); g.addColorStop(1, rgba(m.tint, .34));
        ctx.fillStyle = g; ctx.strokeStyle = rgba(m.tint, .75); ctx.lineWidth = 1.2;
        ctx.beginPath(); ctx.ellipse(0, 0, len / 2, wid, 0, 0, TAU); ctx.fill(); ctx.stroke();
        ctx.strokeStyle = rgba(m.tint, .32); ctx.lineWidth = .8; ctx.beginPath();
        for (let i = 0; i <= 8; i++) { const px = -len * .38 + i * len * .76 / 8, py = (i % 2 ? 1 : -1) * wid * .55; i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); }
        ctx.stroke(); ctx.restore();
      }
      for (const v of env.vesicles) {
        const a = v.a + v.dir * v.k * ph, rr = R * v.rad, size = R * v.size * 2;
        const x = cx + rr * Math.cos(a), y = cy + rr * Math.sin(a);
        ctx.globalAlpha = .8; ctx.drawImage(env.vSprites[v.sprite], x - size / 2, y - size / 2, size, size); ctx.globalAlpha = 1;
      }
      // Nucleus: a dark, calm centre that keeps the words readable, with a double envelope and pores.
      const nr = R * .37 * (1 + .012 * Math.sin(2 * ph));
      const nuc = ctx.createRadialGradient(cx, cy, 0, cx, cy, nr);
      nuc.addColorStop(0, 'rgba(7,21,40,.96)'); nuc.addColorStop(.8, 'rgba(10,32,54,.92)'); nuc.addColorStop(1, 'rgba(26,84,110,.7)');
      ctx.fillStyle = nuc; ctx.beginPath(); ctx.arc(cx, cy, nr, 0, TAU); ctx.fill();
      ctx.strokeStyle = 'rgba(217,248,255,.22)'; ctx.lineWidth = 1.1;
      ctx.beginPath(); ctx.arc(cx, cy, nr, 0, TAU); ctx.stroke();
      ctx.strokeStyle = 'rgba(217,248,255,.10)'; ctx.beginPath(); ctx.arc(cx, cy, nr * 1.05, 0, TAU); ctx.stroke();
      for (let i = 0; i < 28; i++) { const a = i / 28 * TAU; ctx.fillStyle = 'rgba(217,248,255,.35)'; ctx.beginPath(); ctx.arc(cx + nr * 1.025 * Math.cos(a), cy + nr * 1.025 * Math.sin(a), 1.1, 0, TAU); ctx.fill(); }
      ctx.restore();
      // Membrane: iridescent bilayer rim that slowly turns.
      let rim;
      if (ctx.createConicGradient) {
        rim = ctx.createConicGradient(ph, cx, cy);
        ['#71ebff', '#64ffc3', '#f6ff48', '#ff4d86', '#b79cff', '#71ebff'].forEach((c, i, all) => rim.addColorStop(i / (all.length - 1), rgba(c, .85)));
      } else rim = 'rgba(113,235,255,.8)';
      ctx.save();
      ctx.shadowColor = 'rgba(113,235,255,.55)'; ctx.shadowBlur = R * .08;
      ctx.strokeStyle = rim; ctx.lineWidth = Math.max(1.6, R * .012); outline(1); ctx.stroke();
      ctx.restore();
      ctx.strokeStyle = 'rgba(217,248,255,.28)'; ctx.lineWidth = 1; outline(.972); ctx.stroke();
      ctx.strokeStyle = 'rgba(217,248,255,.14)'; outline(1.03); ctx.stroke();
    }
  };

  const ART = { uptake, helix, cell };

  customElements.define('masi-cell-art', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const art = ART[this.dataset.art];
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas?.getContext('2d');
      if (!art || !this.ctx) return;
      this.art = art; this.env = {}; art.init(this.env);
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.loop = LOOPS[this.dataset.art];
      this.time = Number(this.dataset.start || 0);
      this.visible = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.reduced.addEventListener('change', () => this.update(), options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.resizer = new ResizeObserver(() => this.resize());
      this.resizer.observe(this);
      this.watcher = new IntersectionObserver(entries => { this.visible = entries.some(e => e.isIntersecting); this.update(); }, { rootMargin: '80px 0px' });
      this.watcher.observe(this);
      this.resize();
    }
    resize() {
      const box = this.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const ratio = Math.min(devicePixelRatio || 1, 2);
      this.w = box.width; this.h = box.height;
      this.canvas.width = Math.round(box.width * ratio); this.canvas.height = Math.round(box.height * ratio);
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      this.render();
      this.dataset.rendered = 'true';
    }
    render() { if (this.w) this.art.draw(this.ctx, this.w, this.h, this.time % this.loop, this.env); }
    update() {
      cancelAnimationFrame(this.frame); this.last = 0;
      const run = this.visible && !document.hidden && !this.reduced.matches;
      this.dataset.motion = run ? 'running' : 'still';
      if (run) this.frame = requestAnimationFrame(now => this.tick(now));
      else this.render();
    }
    tick(now) {
      if (!this.last) this.last = now;
      const dt = (now - this.last) / 1000;
      if (dt >= 1 / FPS - .002) { this.time = (this.time + Math.min(dt, .1)) % this.loop; this.last = now; this.render(); }
      this.frame = requestAnimationFrame(next => this.tick(next));
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.resizer?.disconnect(); this.watcher?.disconnect(); this.controller?.abort(); this.controller = null;
    }
  });
})();
